import React, { useState, useCallback, useMemo } from 'react';
import { Map, AdvancedMarker, InfoWindow, useAdvancedMarkerRef } from '@vis.gl/react-google-maps';
import { ImgWithSkeleton } from './ui/ImgWithSkeleton';

interface SimpleVenueMapProps {
  venue: {
    id: string;
    name: string;
    coordinates?: { lat: number; lng: number } | null;
    address: {
      street: string;
      city: string;
      state: string;
      zipCode: string;
    };
    firstHeroImage?: {
      cloudinaryPublicId: string;
      cloudinaryUrl: string;
    } | null;
  };
  className?: string;
}

export const SimpleVenueMap = ({ venue, className }: SimpleVenueMapProps) => {
  const [markerRef, marker] = useAdvancedMarkerRef();
  const [infoWindowOpen, setInfoWindowOpen] = useState(true);

  // Determine if venue has valid coordinates
  const hasCoordinates = useMemo(() => {
    return (
      venue.coordinates &&
      typeof venue.coordinates.lat === 'number' &&
      typeof venue.coordinates.lng === 'number' &&
      !isNaN(venue.coordinates.lat) &&
      !isNaN(venue.coordinates.lng) &&
      venue.coordinates.lat >= -90 &&
      venue.coordinates.lat <= 90 &&
      venue.coordinates.lng >= -180 &&
      venue.coordinates.lng <= 180
    );
  }, [venue.coordinates]);

  // Build full address
  const fullAddress = useMemo(() => {
    const addressParts = [
      venue.address.street,
      venue.address.city,
      venue.address.state,
      venue.address.zipCode,
    ].filter(Boolean);
    return addressParts.join(', ');
  }, [venue.address]);

  const handleMarkerClick = useCallback(() => {
    setInfoWindowOpen(true);
  }, []);

  const handleInfoWindowClose = useCallback(() => {
    setInfoWindowOpen(false);
  }, []);

  // If no coordinates, show fallback
  if (!hasCoordinates) {
    return (
      <div className={className}>
        <div className="relative bg-gray-100 rounded-lg overflow-hidden h-full min-h-[300px]">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex flex-col items-center space-y-4 p-6 text-center">
              <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-2">
                  Location not available
                </h3>
                <p className="text-xs text-gray-600 max-w-xs mb-4">
                  Map location is not available for this venue.
                </p>
                <div className="bg-gray-50 rounded-lg p-4 text-left border border-gray-200">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Address</h4>
                  <div className="text-sm text-gray-700 space-y-1">
                    <div>{venue.address.street}</div>
                    <div>
                      {venue.address.city}, {venue.address.state} {venue.address.zipCode}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <Map
        defaultCenter={venue.coordinates}
        defaultZoom={15}
        mapId="DEMO_MAP_ID"
        gestureHandling="greedy"
        disableDefaultUI={false}
        className="w-full h-full min-h-[300px] rounded-lg"
      >
        <AdvancedMarker
          ref={markerRef}
          position={venue.coordinates}
          title={venue.name}
          onClick={handleMarkerClick}
        />

        {infoWindowOpen && marker && (
          <InfoWindow
            anchor={marker}
            onCloseClick={handleInfoWindowClose}
            maxWidth={300}
            headerContent=<h4>{venue.name}</h4>
          >
            <div className="p-0 min-w-[250px]">
              {/* Hero image section */}
              {venue.firstHeroImage?.cloudinaryUrl && (
                <div className="w-full mb-3 rounded-lg overflow-hidden">
                  <ImgWithSkeleton
                    src={venue.firstHeroImage.cloudinaryUrl}
                    alt={venue.name}
                    className="w-full h-32 object-cover"
                    skeletonClassName="rounded-lg"
                  />
                </div>
              )}

              {/* Venue info */}
              <div>
                <p className="text-xs text-gray-600 mb-3 mt-1">{fullAddress}</p>
              </div>
            </div>
          </InfoWindow>
        )}
      </Map>
    </div>
  );
};
